import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Card template types
type CardTemplate = 'project' | 'gallery' | 'ticket' | 'member' | 'content' | 'custom';
type LayoutVariant = 'list' | 'grid' | 'rows';

interface GridColumns {
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

interface CardListSkeletonProps {
  variant?: LayoutVariant;
  count?: number;
  columns?: GridColumns;
  gap?: number;
  cardTemplate?: CardTemplate;
  renderCard?: () => React.ReactNode;
  className?: string;
}

// Template Components
const ProjectCardSkeleton = () => (
  <div className="border border-border rounded-lg">
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-3 flex-1">
        <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
        <div className="flex-1 min-w-0 space-y-1.5">
          <Skeleton className="h-5 w-48 max-w-full" />
          <Skeleton className="h-3 w-32 max-w-[80%]" />
        </div>
      </div>
      <Skeleton className="w-8 h-8 rounded-md shrink-0" />
    </div>
  </div>
);

const GalleryCardSkeleton = () => (
  <div className="rounded-2xl border border-border bg-card overflow-hidden">
    <div className="aspect-[4/3]">
      <Skeleton className="w-full h-full" />
    </div>
    <div className="p-4 space-y-3">
      <div className="space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="w-6 h-6 rounded-full" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="h-4 w-6" />
          </div>
          <div className="flex items-center gap-1">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="h-4 w-6" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TicketCardSkeleton = () => (
  <div className="p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
    <div className="flex items-start justify-between gap-2 mb-2">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-5 w-14 rounded-full shrink-0" />
    </div>
    <Skeleton className="h-4 w-full mb-1" />
    <Skeleton className="h-4 w-2/3 mb-2" />
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-16 rounded-full" />
      <Skeleton className="h-3 w-20" />
    </div>
  </div>
);

const MemberCardSkeleton = () => (
  <div className="flex items-center justify-between p-4 border-b border-border last:border-0">
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-full shrink-0" />
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-40" />
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Skeleton className="h-5 w-20 rounded-full" />
      <Skeleton className="w-8 h-8 rounded-md" />
    </div>
  </div>
);

const ContentCardSkeleton = () => (
  <div className="space-y-4 p-6 border border-border rounded-lg">
    <div className="space-y-2">
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
    </div>
    <div className="grid grid-cols-2 gap-3 pt-2">
      <Skeleton className="h-20 rounded-md" />
      <Skeleton className="h-20 rounded-md" />
    </div>
  </div>
);

// Template map
const CARD_TEMPLATES: Record<Exclude<CardTemplate, 'custom'>, React.FC> = {
  project: ProjectCardSkeleton,
  gallery: GalleryCardSkeleton,
  ticket: TicketCardSkeleton,
  member: MemberCardSkeleton,
  content: ContentCardSkeleton,
};

// Grid column class generator
const getGridClasses = (columns: GridColumns = {}): string => {
  const classes: string[] = ['grid'];
  
  if (columns.sm) classes.push(`sm:grid-cols-${columns.sm}`);
  if (columns.md) classes.push(`md:grid-cols-${columns.md}`);
  if (columns.lg) classes.push(`lg:grid-cols-${columns.lg}`);
  if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`);
  
  // Default to single column if no columns specified
  if (!columns.sm && !columns.md && !columns.lg && !columns.xl) {
    classes.push('grid-cols-1');
  }
  
  return classes.join(' ');
};

export function CardListSkeleton({
  variant = 'list',
  count = 3,
  columns = { sm: 2, lg: 3, xl: 4 },
  gap = 4,
  cardTemplate = 'project',
  renderCard,
  className,
}: CardListSkeletonProps) {
  const CardComponent = cardTemplate === 'custom' 
    ? null 
    : CARD_TEMPLATES[cardTemplate];

  const renderCardItem = (index: number) => {
    if (cardTemplate === 'custom' && renderCard) {
      return <div key={index}>{renderCard()}</div>;
    }
    if (CardComponent) {
      return <CardComponent key={index} />;
    }
    return null;
  };

  const items = Array.from({ length: count }, (_, i) => renderCardItem(i));

  // Layout variants
  if (variant === 'grid') {
    return (
      <div 
        className={cn(
          getGridClasses(columns),
          `gap-${gap}`,
          className
        )}
        style={{ gap: `${gap * 4}px` }}
      >
        {items}
      </div>
    );
  }

  if (variant === 'rows') {
    return (
      <div className={cn('divide-y divide-border rounded-lg border border-border', className)}>
        {items}
      </div>
    );
  }

  // Default: list
  return (
    <div 
      className={cn('space-y-3', className)}
      style={{ gap: `${gap * 4}px` }}
    >
      {items}
    </div>
  );
}

// Export individual templates for direct use
export { 
  ProjectCardSkeleton, 
  GalleryCardSkeleton, 
  TicketCardSkeleton, 
  MemberCardSkeleton, 
  ContentCardSkeleton 
};
